import { axiosRequest, type AxiosResult } from "@/lib/axios";
import type { Widget, WidgetRequest, WidgetUpdateRequest, WidgetPreviewRequest, WidgetPreviewResponse, WidgetTemplateListItem, WidgetTemplateDetail, WidgetAggregationPreviewRequest, WidgetAggregationPreviewResponse } from "../types";

const API_BASE_URL = import.meta.env.VITE_SERVICE_CORE_URL;

export const widgetApi = {
	index: (workspaceId: string): Promise<AxiosResult<Widget[]>> => {
		return axiosRequest({ url: `${API_BASE_URL}/${workspaceId}/widgets`, method: "GET" });
	},

	show: (id: string, workspaceId: string): Promise<AxiosResult<Widget>> => {
		return axiosRequest({ url: `${API_BASE_URL}/${workspaceId}/widgets/${id}`, method: "GET" });
	},

	create: (workspaceId: string, payload: WidgetRequest): Promise<AxiosResult<Widget>> => {
		return axiosRequest({ url: `${API_BASE_URL}/${workspaceId}/widgets`, method: "POST", data: payload });
	},

	update: (id: string, workspaceId: string, payload: WidgetUpdateRequest): Promise<AxiosResult<Widget>> => {
		return axiosRequest({ url: `${API_BASE_URL}/${workspaceId}/widgets/${id}`, method: "PATCH", data: payload });
	},

	delete: (id: string, workspaceId: string): Promise<AxiosResult<void>> => {
		return axiosRequest({ url: `${API_BASE_URL}/${workspaceId}/widgets/${id}`, method: "DELETE" });
	},

	preview: (workspaceId: string, payload: WidgetPreviewRequest, cursor?: string | null): Promise<AxiosResult<WidgetPreviewResponse>> => {
		const url = cursor
			? `${API_BASE_URL}/${workspaceId}/widgets/preview?cursor=${encodeURIComponent(cursor)}`
			: `${API_BASE_URL}/${workspaceId}/widgets/preview`;
		return axiosRequest({ url, method: "POST", data: payload });
	},

	listTemplates: (): Promise<AxiosResult<WidgetTemplateListItem[]>> => {
		return axiosRequest({ url: `${API_BASE_URL}/widget-templates`, method: "GET" });
	},

	showTemplate: (template: string, version: number): Promise<AxiosResult<WidgetTemplateDetail>> => {
		return axiosRequest({ url: `${API_BASE_URL}/widget-templates/${template}/${version}`, method: "GET" });
	},

	indexPinnedReviewIds: (workspaceId: string, widgetId: string): Promise<AxiosResult<string[]>> => {
		return axiosRequest({ url: `${API_BASE_URL}/${workspaceId}/widgets/${widgetId}/reviews-pinned/ids`, method: "GET" });
	},

	indexCuratedReviewIds: (workspaceId: string, widgetId: string): Promise<AxiosResult<string[]>> => {
		return axiosRequest({ url: `${API_BASE_URL}/${workspaceId}/widgets/${widgetId}/reviews-curated/ids`, method: "GET" });
	},

	syncPinnedReviews: (workspaceId: string, widgetId: string, reviewIds: string[]): Promise<AxiosResult<void>> => {
		return axiosRequest({
			url: `${API_BASE_URL}/${workspaceId}/widgets/${widgetId}/reviews-pinned`,
			method: "PUT",
			data: { review_ids: reviewIds },
		});
	},

	syncCuratedReviews: (workspaceId: string, widgetId: string, reviewIds: string[]): Promise<AxiosResult<void>> => {
		return axiosRequest({
			url: `${API_BASE_URL}/${workspaceId}/widgets/${widgetId}/reviews-curated`,
			method: "PUT",
			data: { review_ids: reviewIds },
		});
	},

	aggregationPreview: (workspaceId: string, payload: WidgetAggregationPreviewRequest): Promise<AxiosResult<WidgetAggregationPreviewResponse>> => {
		return axiosRequest({
			url: `${API_BASE_URL}/${workspaceId}/widgets/aggregation-preview`,
			method: "POST",
			data: payload,
		});
	},
};
